import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { Template, TemplateWithAccess } from '../types/template';

// Global template refresh registry
let globalTemplateRefresh: (() => void) | null = null;

/**
 * Trigger a global refresh of templates from anywhere in the app
 */
export const refreshTemplates = () => {
  if (globalTemplateRefresh) {
    globalTemplateRefresh();
  }
};

/**
 * Hook for fetching and managing project templates
 */
export const useTemplates = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [templates, setTemplates] = useState<TemplateWithAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch templates from the API with optional filtering
   * @param category - Optional category filter
   * @param includeInactive - Whether to include inactive templates (default: false)
   */
  const fetchTemplates = useCallback(async (category?: string, includeInactive: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (category) {
        params.append('category', category);
      }
      if (includeInactive) {
        params.append('include_inactive', 'true');
      }

      const queryString = params.toString();
      const endpoint = queryString ? `${apiEndpoints.templates}?${queryString}` : apiEndpoints.templates;
      
      const data = await authenticatedApi.get<TemplateWithAccess[]>(endpoint);
      
      setTemplates(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch templates'));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi]);

  /**
   * Get a specific template by ID
   * @param templateId - The template ID to fetch
   */
  const getTemplate = useCallback(async (templateId: string): Promise<Template | null> => {
    try {
      const data = await authenticatedApi.get<Template>(apiEndpoints.template(templateId));
      return data;
    } catch (err) {
      console.error('Error fetching template:', err);
      throw err instanceof Error ? err : new Error('Failed to fetch template');
    }
  }, [authenticatedApi]);

  /**
   * Filter templates by category from the current list
   * @param category - The category to filter by
   */
  const getTemplatesByCategory = useCallback((category: string): Template[] => {
    return templates.filter(template => template.category === category);
  }, [templates]);

  /**
   * Get all unique categories from the current templates list
   */
  const getAvailableCategories = useCallback((): string[] => {
    const categories = templates.map(template => template.category);
    return Array.from(new Set(categories)).sort();
  }, [templates]);

  /**
   * Refresh templates (refetch from API)
   */
  const refetch = useCallback((category?: string, includeInactive?: boolean) => {
    return fetchTemplates(category, includeInactive);
  }, [fetchTemplates]);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Register global refresh function
  useEffect(() => {
    globalTemplateRefresh = () => fetchTemplates();
    return () => {
      globalTemplateRefresh = null;
    };
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    getTemplate,
    getTemplatesByCategory,
    getAvailableCategories,
    refetch,
  };
}; 